import { useEffect, useRef, useState } from 'react';

/**
 * True from mount until the grid observes its first completed load, then false for the rest of the mount —
 * later refetches (paging, sorting, searching) keep the grid on screen instead of flashing the spinner.
 * Watching isLoading rather than returning it directly covers the pre-dispatch mount render, where the fetch
 * effect has not run yet and isLoading is still false — the pre-load flash this hook exists to close.
 *
 * Pass `viewId` when one component serves several views (the LTL page's status tabs): a changed id resets the
 * gate, so the spinner covers the in-flight window while the slice still holds the previous view's rows.
 */
export const useShouldShowSpinner = (isLoading: boolean, viewId?: string) => {
  const [hasCompletedLoad, setHasCompletedLoad] = useState(false);
  const [loadedViewId, setLoadedViewId] = useState(viewId);
  const hasSeenLoadStartRef = useRef(false);

  if (viewId !== loadedViewId) {
    setLoadedViewId(viewId);
    setHasCompletedLoad(false);
    hasSeenLoadStartRef.current = false;
  }

  useEffect(() => {
    if (isLoading) {
      hasSeenLoadStartRef.current = true;
    } else if (hasSeenLoadStartRef.current) {
      setHasCompletedLoad(true);
    }
  }, [isLoading]);

  return !hasCompletedLoad;
};
